import React from 'react';
import { useSelector } from 'react-redux';
import { loadColumnsForTable } from '@/actions';
import { getDatasourceTables } from '@/store-selectors';
import ButtonWithClose from '@/components/ButtonWithClose';
import ColumnsSelector from '@/components/ColumnsSelector';
import TableRef from '@/util/TableRef';
import {
  changeMode,
  SAVED_QUERIES,
  SUGGESTED,
} from '@/features/joinModal/joinModalSlice';
import { useAppDispatch } from '@/store/hooks';
import ForeignKey from '@/util/ForeignKey';
import ColumnRef from '@/util/ColumnRef';
import { usePrefetchSavedQueries } from '@/features/savedQueriesWidget/hooks';
import type { JoinModeProps } from '../../types';
import { CombineRows } from '../CombineRows';

const loadJoinSavedQuery = () => import('../JoinSavedQuery');

export const JoinTable: React.FC<JoinModeProps> = ({
  children,
  userInteraction,
  state,
  columns,
  usedAliases,
  renderer,
}) => {
  const dispatch = useAppDispatch();
  const prefetchSavedQueries = usePrefetchSavedQueries();
  const tables = useSelector(getDatasourceTables);

  const tableChosen = (tableRef: TableRef) => (e: React.MouseEvent) => {
    e.preventDefault();
    const props = { section: 'tableChosen', table: tableRef };
    dispatch(loadColumnsForTable(tableRef));
    userInteraction(props)(undefined);
  };

  const handlePrefetchSavedQueries = () => {
    loadJoinSavedQuery().then(prefetchSavedQueries);
  };

  const handleSwitchToJoinSuggested = (e: React.MouseEvent) => {
    userInteraction({ section: 'cancelAdvanced' })(e);
    dispatch(changeMode(SUGGESTED));
  };

  const handleSwitchToJoinSavedQueries = (e: React.MouseEvent) => {
    userInteraction({ section: 'toggleView' })(e);
    dispatch(changeMode(SAVED_QUERIES));
  };

  if (state.selected) {
    return (
      <>
        <div className="margin-bottom-20">
          <ButtonWithClose
            userInteraction={userInteraction}
            data={{ section: 'deselect' }}
            text={renderer.renderValue(state.selected)}
            tooltip={renderer.renderValue(state.selected, true)}
          />
        </div>
        {children}
      </>
    );
  }

  if (state.joinType === 'cross') {
    return (
      <>
        <p className="padding-5">
          Choose a table (
          <span>
            <a
              href="#"
              onClick={handleSwitchToJoinSavedQueries}
              onMouseEnter={handlePrefetchSavedQueries}
            >
              or a saved query
            </a>
          </span>
          ) to join with:
        </p>
        <ColumnsSelector
          values={tables.map(
            (t) => new ForeignKey(null, new ColumnRef(t, null)),
          )}
          selectedValue={state.selected}
          renderer={renderer}
        />
        {children}
      </>
    );
  }

  if (state.selectedTov) {
    return (
      <CombineRows
        state={state}
        columns={columns}
        userInteraction={userInteraction}
        usedAliases={usedAliases}
      />
    );
  }

  return (
    <div>
      <div>
        <p className="padding-5">
          <b>Join a table</b> (
          <span>
            <a
              href="#"
              onClick={handleSwitchToJoinSavedQueries}
              onMouseEnter={handlePrefetchSavedQueries}
            >
              or join a saved query
            </a>
          </span>
          )
        </p>
        <div className="list-group">
          {tables.map((t) => (
            <a
              key={`${t.schema()}.${t.name()}`}
              href="#"
              className="list-group-item"
              onClick={tableChosen(t)}
            >
              <b>{t.name()}</b>{' '}
              {t.schema() === 'public' ? null : (
                <span className="faint">{t.schema()} </span>
              )}
              {state.joinType === 'cross' && '(on * = *)'}
            </a>
          ))}
        </div>
      </div>
      {children}
      {!state.selected && <hr />}
      <div className="clear-both padding-5">
        <a href="#" onClick={handleSwitchToJoinSuggested}>
          Cancel
        </a>
      </div>
    </div>
  );
};
